import React, { createContext, useContext, useState, ReactNode, useMemo, useCallback } from 'react';

interface EditedWorkflowStateContextValue {
  editedWorkflowIds: number[];
  clearedWorkflowIds: number[];
  isWorkflowEdited: (id: number) => boolean;
}

interface EditedWorkflowDispatchContextValue {
  markWorkflowAsEdited: (id: number) => void;
  removeWorkflowFromEdited: (id: number, addToCleared?: boolean) => void;
  upsertEditedWorkflow: (id: number) => void;
}

const EditedWorkflowStateContext = createContext<EditedWorkflowStateContextValue | undefined>(undefined);

const EditedWorkflowDispatchContext = createContext<EditedWorkflowDispatchContextValue | undefined>(undefined);

const EditedWorkflowsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [editedWorkflowIds, setEditedWorkflowIds] = useState<number[]>([]);
  const [clearedWorkflowIds, setClearedWorkflowIds] = useState<number[]>([]);

  const markWorkflowAsEdited = useCallback((id: number) => {
    setEditedWorkflowIds((prevIds) => {
      if (!prevIds.includes(id)) {
        return [...prevIds, id];
      }
      return prevIds;
    });
    setClearedWorkflowIds((prevClearedWorkflowIds) =>
      prevClearedWorkflowIds.includes(id)
        ? prevClearedWorkflowIds.filter((workflowId) => workflowId !== id)
        : prevClearedWorkflowIds
    );
  }, []);

  const removeWorkflowFromEdited = useCallback((id: number, addToCleared?: boolean) => {
    setEditedWorkflowIds((prevIds) => prevIds.filter((workflowId) => workflowId !== id));

    if (addToCleared) {
      setClearedWorkflowIds((prevClearedWorkflowIds) => [...prevClearedWorkflowIds, id]);
    }
  }, []);

  const upsertEditedWorkflow = useCallback((id: number) => {
    setEditedWorkflowIds((prevIds) => {
      if (!prevIds.includes(id)) {
        return [...prevIds, id];
      }
      return prevIds;
    });
    setClearedWorkflowIds((prevClearedWorkflowIds) =>
      prevClearedWorkflowIds.includes(id)
        ? prevClearedWorkflowIds.filter((workflowId) => workflowId !== id)
        : prevClearedWorkflowIds
    );
  }, []);

  const isWorkflowEdited = useCallback((id: number) => editedWorkflowIds.includes(id), [editedWorkflowIds]);

  const stateValue = useMemo(
    () => ({ editedWorkflowIds, clearedWorkflowIds, isWorkflowEdited }),
    [editedWorkflowIds, clearedWorkflowIds, isWorkflowEdited]
  );
  const dispatchValue = useMemo(
    () => ({ markWorkflowAsEdited, removeWorkflowFromEdited, upsertEditedWorkflow }),
    [markWorkflowAsEdited, removeWorkflowFromEdited, upsertEditedWorkflow]
  );

  return (
    <EditedWorkflowStateContext.Provider value={stateValue}>
      <EditedWorkflowDispatchContext.Provider value={dispatchValue}>{children}</EditedWorkflowDispatchContext.Provider>
    </EditedWorkflowStateContext.Provider>
  );
};

const useEditedWorkflowState = () => {
  const context = useContext(EditedWorkflowStateContext);
  if (context === undefined) {
    throw new Error('useEditedWorkflowState must be used within a EditedWorkflowsProvider');
  }
  return context;
};

const useEditedWorkflowActions = () => {
  const context = useContext(EditedWorkflowDispatchContext);
  if (context === undefined) {
    throw new Error('useEditedWorkflowActions must be used within a EditedWorkflowsProvider');
  }
  return context;
};

export { EditedWorkflowsProvider, useEditedWorkflowState, useEditedWorkflowActions };
